// src/components/LegendCreator.js
import React, { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { getDistance } from 'geolib';

/**
 * LegendCreator receives the list of markers and the totalDistance
 * so it can update the legend content accordingly.
 */
const LegendCreator = ({ markers, totalDistance }) => {
  const map = useMap();        // Get the Leaflet map instance from context
  const legendRef = useRef(null); // We'll store a reference to the <div> used by the legend

  // 1. Create the legend control once, when the map becomes available
  useEffect(() => {
    if (!map) return;

    console.log("Creating legend in LegendCreator, map:", map);

    // Create a Leaflet control for the legend
    const legendControl = L.control({ position: 'topright' });

    // Create a <div> for the legend
    const div = L.DomUtil.create('div', 'legend-control');
    div.id = 'legend-control';

    // Basic styling to ensure it is visible
    div.style.backgroundColor = 'white';
    div.style.padding = '10px';
    div.style.border = '2px solid #000';
    div.style.fontSize = '14px';

    // Initial placeholder text
    div.innerHTML = "<strong>Legend</strong><br>No markers yet.";

    // Attach the <div> to the control
    legendControl.onAdd = () => div;
    legendControl.addTo(map);
    console.log("Legend control added to the map.");

    // Store the div in our ref so we can update it later
    legendRef.current = div;

    // Cleanup if the component unmounts
    return () => {
      console.log("Removing legend control from the map.");
      legendControl.remove();
    };
  }, [map]);

  // 2. Update the legend content whenever markers or totalDistance change
  useEffect(() => {
    if (!legendRef.current) return; // If no div yet, skip

    // Build the HTML
    let html = "<strong>Legend</strong><br>";
    if (markers.length === 0) {
      html += "No markers yet.";
    } else {
      html += `<strong>Number of markers:</strong> ${markers.length}<br>`;
      // Optionally list each marker
      markers.forEach((marker, index) => {
        const [lat, lng] = marker;
        html += `Marker ${index + 1}: (${lat.toFixed(7)}, ${lng.toFixed(7)})<br>`;
        // Add distance to next marker if it exists
        if (index < markers.length - 1) {
            const next = markers[index + 1];
            const pointA = L.latLng(marker);
            const pointB = L.latLng(next);
            {/*const distance = pointA.distanceTo(pointB) * 3.28084; // meters to feet*/}
            const distance = getDistance(
                { latitude: pointA.lat, longitude: pointA.lng },
                { latitude: pointB.lat, longitude: pointB.lng }
              ) * 3.28084;
            html += `&nbsp;&nbsp;&rarr; Distance: ${distance.toFixed(2)} ft<br>`;
        }
      });
      html += `<br><strong>Total Distance:</strong> ${totalDistance} ft`;
    }

    // Update the div content
    legendRef.current.innerHTML = html;
  }, [markers, totalDistance]);

  return null;  // This component doesn't render anything directly
};

export default LegendCreator;